/**
 * PDF Export Utility for DEMS Audit Reports
 * 
 * Generates formatted PDF reports using browser print functionality.
 * For production use, consider integrating jsPDF or similar library.
 */

import { format } from 'date-fns';
import { CustodyChainEvent } from '../api/client';

interface AuditReportData {
  title: string;
  generatedBy: string;
  generatedAt: Date;
  filters: {
    dateRange?: { from: string; to: string };
    eventType?: string;
    user?: string;
    caseNumber?: string;
  };
  events: CustodyChainEvent[];
  summary: {
    totalEvents: number;
    byEventType: Record<string, number>;
  };
}

/**
 * Generates HTML for a printable audit report
 */
export function generateAuditReportHtml(data: AuditReportData): string {
  const eventTypeColors: Record<string, string> = {
    upload: '#16a34a',
    download: '#2563eb',
    view: '#6b7280',
    transfer: '#7c3aed',
    legal_hold: '#d97706',
    integrity_check: '#9333ea',
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${data.title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 11px;
          line-height: 1.5;
          color: #1f2937;
          padding: 20px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #1e3a5f;
        }
        .header h1 {
          font-size: 20px;
          color: #1e3a5f;
          margin-bottom: 4px;
        }
        .header .subtitle {
          font-size: 12px;
          color: #6b7280;
        }
        .header .logo {
          text-align: right;
        }
        .header .logo .system-name {
          font-size: 14px;
          font-weight: 600;
          color: #1e3a5f;
        }
        .meta {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 4px;
        }
        .meta-item {
          display: flex;
          flex-direction: column;
        }
        .meta-item .label {
          font-size: 9px;
          text-transform: uppercase;
          color: #6b7280;
          margin-bottom: 2px;
        }
        .meta-item .value {
          font-weight: 500;
        }
        .summary {
          margin-bottom: 24px;
        }
        .summary h2 {
          font-size: 14px;
          margin-bottom: 12px;
          color: #374151;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }
        .stat-box {
          padding: 12px;
          background: #f9fafb;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }
        .stat-box .label {
          font-size: 9px;
          text-transform: uppercase;
          color: #6b7280;
        }
        .stat-box .value {
          font-size: 20px;
          font-weight: 600;
          color: #1e3a5f;
        }
        .events-section h2 {
          font-size: 14px;
          margin-bottom: 12px;
          color: #374151;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          background: #1e3a5f;
          color: white;
          padding: 8px 12px;
          text-align: left;
          font-size: 10px;
          text-transform: uppercase;
        }
        td {
          padding: 8px 12px;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }
        tr:nth-child(even) {
          background: #f9fafb;
        }
        .event-type {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .footer {
          margin-top: 32px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          font-size: 9px;
          color: #6b7280;
          display: flex;
          justify-content: space-between;
        }
        .signature-line {
          margin-top: 48px;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 48px;
        }
        .signature-box {
          border-top: 1px solid #1f2937;
          padding-top: 8px;
        }
        .signature-box .label {
          font-size: 10px;
          color: #6b7280;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1>${data.title}</h1>
          <p class="subtitle">Chain of Custody Audit Report</p>
        </div>
        <div class="logo">
          <div class="system-name">DEMS</div>
          <div style="font-size: 9px; color: #6b7280;">Digital Evidence<br>Management System</div>
        </div>
      </div>

      <div class="meta">
        <div class="meta-item">
          <span class="label">Generated By</span>
          <span class="value">${data.generatedBy}</span>
        </div>
        <div class="meta-item">
          <span class="label">Generated At</span>
          <span class="value">${format(data.generatedAt, 'MMMM d, yyyy h:mm a')}</span>
        </div>
        ${data.filters.dateRange ? `
        <div class="meta-item">
          <span class="label">Date Range</span>
          <span class="value">${data.filters.dateRange.from} to ${data.filters.dateRange.to}</span>
        </div>
        ` : ''}
        ${data.filters.eventType ? `
        <div class="meta-item">
          <span class="label">Event Type Filter</span>
          <span class="value" style="text-transform: capitalize;">${data.filters.eventType.replace('_', ' ')}</span>
        </div>
        ` : ''}
        ${data.filters.user ? `
        <div class="meta-item">
          <span class="label">User Filter</span>
          <span class="value">${data.filters.user}</span>
        </div>
        ` : ''}
        ${data.filters.caseNumber ? `
        <div class="meta-item">
          <span class="label">Case Filter</span>
          <span class="value">${data.filters.caseNumber}</span>
        </div>
        ` : ''}
      </div>

      <div class="summary">
        <h2>Summary</h2>
        <div class="summary-grid">
          <div class="stat-box">
            <div class="label">Total Events</div>
            <div class="value">${data.summary.totalEvents}</div>
          </div>
          <div class="stat-box">
            <div class="label">Uploads</div>
            <div class="value">${data.summary.byEventType.upload || 0}</div>
          </div>
          <div class="stat-box">
            <div class="label">Downloads</div>
            <div class="value">${data.summary.byEventType.download || 0}</div>
          </div>
          <div class="stat-box">
            <div class="label">Integrity Checks</div>
            <div class="value">${data.summary.byEventType.integrity_check || 0}</div>
          </div>
        </div>
      </div>

      <div class="events-section">
        <h2>Audit Events (${data.events.length})</h2>
        <table>
          <thead>
            <tr>
              <th style="width: 15%;">Timestamp</th>
              <th style="width: 12%;">Event Type</th>
              <th style="width: 35%;">Description</th>
              <th style="width: 18%;">Performed By</th>
              <th style="width: 20%;">Evidence ID</th>
            </tr>
          </thead>
          <tbody>
            ${data.events.map(event => `
              <tr>
                <td>${format(new Date(event.performedAt), 'MMM d, yyyy h:mm a')}</td>
                <td>
                  <span class="event-type" style="background: ${eventTypeColors[event.eventType] || '#6b7280'}20; color: ${eventTypeColors[event.eventType] || '#6b7280'};">
                    ${event.eventType.replace('_', ' ')}
                  </span>
                </td>
                <td>${event.description}</td>
                <td>${event.performedByName}</td>
                <td style="font-family: monospace; font-size: 9px;">${event.evidenceId.slice(0, 16)}...</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="signature-line">
        <div class="signature-box">
          <div class="label">Authorized Signature</div>
        </div>
        <div class="signature-box">
          <div class="label">Date</div>
        </div>
      </div>

      <div class="footer">
        <span>DEMS Audit Report - Confidential</span>
        <span>Page 1 of 1</span>
      </div>
    </body>
    </html>
  `;
}

/**
 * Opens a new window with the PDF-ready report for printing
 */
export function exportAuditReportPdf(data: AuditReportData): void {
  const html = generateAuditReportHtml(data);
  const printWindow = window.open('', '_blank');
  
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    
    // Wait for content to load then trigger print
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}

/**
 * Exports audit events as CSV
 */
export function exportAuditReportCsv(events: CustodyChainEvent[], filename?: string): void {
  const csv = [
    ['Timestamp', 'Event Type', 'Description', 'Performed By', 'Evidence ID', 'IP Address'].join(','),
    ...events.map(e =>
      [
        format(new Date(e.performedAt), 'yyyy-MM-dd HH:mm:ss'),
        e.eventType,
        `"${e.description.replace(/"/g, '""')}"`,
        e.performedByName,
        e.evidenceId,
        e.ipAddress || '',
      ].join(',')
    ),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `dems-audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default {
  generateAuditReportHtml,
  exportAuditReportPdf,
  exportAuditReportCsv,
};
